import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useEffect, useState} from 'react';

import {head, innerJoin, isNil} from 'ramda';
import {isEmptyArray, isNotNilOrEmpty} from 'ramda-adjunct';

import {FileResponseBody} from '@dms/api/shared';
import {
  useLazyGetVehicleWarehouseMovementQuery,
  useListAvailableVehicleWarehousesForVehicleMovementQuery,
  useListVehicleWarehousesQuery,
  useTransferVehicleWarehouseMovementMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {buildArray, getApiDateString, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface VehicleWarehouseTransferProps extends TestIdProps {
  vehicleId: string;
  saleVehicleId: string;
  onDocumentCreation: (documents: FileResponseBody[]) => void;
  onMovementComplete?: () => void;
  branchId?: string;
}

interface VehicleWarehouseTransferFormFields {
  vehicleWarehouseId: string;
  movementAt: string;
  note: string | null;
}

export function VehicleWarehouseTransfer(props: VehicleWarehouseTransferProps) {
  const isWithBranchId = isNotNilOrEmpty(props.branchId);

  const [isStockInDocumentSelected, setIsStockInDocumentSelected] = useState(true);
  const [isStockOutDocumentSelected, setIsStockOutDocumentSelected] = useState(true);
  const [stockInDocumentTemplate, setStockInDocumentTemplate] = useState<TemplateBoxTemplate>();
  const [stockOutDocumentTemplate, setStockOutDocumentTemplate] = useState<TemplateBoxTemplate>();

  const [getTemplates, {isLoading: isTemplatesLoading, isError: isTemplatesError}] =
    useDocumentTemplates();
  const {
    defaultTemplates: stockInDefaultTemplates,
    selectedTemplates: stockInSelectedTemplates,
    selectedAuthProfileLabel: stockInSelectedAuthProfileLabel,
    primaryTemplate: stockInPrimaryTemplate,
  } = getTemplates('receive-vehicle-warehouse-movement');
  const {
    defaultTemplates: stockOutDefaultTemplates,
    selectedTemplates: stockOutSelectedTemplates,
    selectedAuthProfileLabel: stockOutSelectedAuthProfileLabel,
    primaryTemplate: stockOutPrimaryTemplate,
  } = getTemplates('issue-vehicle-warehouse-movement');

  const {
    data: warehouses = [],
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useListAvailableVehicleWarehousesForVehicleMovementQuery({
    vehicleId: props.vehicleId,
  });

  const {data: warehousesByBranch = []} = useListVehicleWarehousesQuery(
    {
      branchId: props.branchId,
    },
    {
      skip: isNil(props.branchId),
    }
  );

  const availableWarehouses = isWithBranchId
    ? innerJoin(
        (warehouseByUser, warehouseByBranch) => warehouseByUser.id === warehouseByBranch.id,
        warehouses,
        warehousesByBranch
      )
    : warehouses;

  const [transferVehicle] = useTransferVehicleWarehouseMovementMutation();
  const [getMovement] = useLazyGetVehicleWarehouseMovementQuery();

  useEffect(
    function selectPrimaryStockInTemplateByDefault() {
      if (isNotNilOrEmpty(stockInDocumentTemplate)) {
        return;
      }

      setStockInDocumentTemplate(stockInPrimaryTemplate);
    },
    [stockInPrimaryTemplate, stockInDocumentTemplate]
  );

  useEffect(
    function selectPrimaryStockOutTemplateByDefault() {
      if (isNotNilOrEmpty(stockOutDocumentTemplate)) {
        return;
      }

      setStockOutDocumentTemplate(stockOutPrimaryTemplate);
    },
    [stockOutPrimaryTemplate, stockOutDocumentTemplate]
  );

  const warehouseOptions = availableWarehouses.map((warehouse) => ({
    label: warehouse.name,
    value: warehouse.id,
  }));

  const onSubmit: FormSubmitHandler<VehicleWarehouseTransferFormFields> = (values) =>
    transferVehicle({
      vehicleId: props.vehicleId,
      saleVehicleId: props.saleVehicleId,
      body: {
        toVehicleWarehouseId: values.vehicleWarehouseId,
        movementAt: values.movementAt,
        issueTemplateId: isStockOutDocumentSelected ? stockOutDocumentTemplate?.id : undefined,
        receiptTemplateId: isStockInDocumentSelected ? stockInDocumentTemplate?.id : undefined,
        note: values.note,
      },
    })
      .unwrap()
      .then((response) => {
        if (!isStockInDocumentSelected && !isStockOutDocumentSelected) {
          showNotification.success(i18n.t('entity.vehicle.labels.vehicleTransferred'));
          closeCurrentDialog();

          return;
        }

        return response;
      })
      .then((response) => {
        if (!response) {
          return;
        }

        return Promise.all([
          getMovement({vehicleWarehouseMovementId: response.receiptMovementId}).unwrap(),
          getMovement({vehicleWarehouseMovementId: response.issueMovementId}).unwrap(),
        ])
          .then(([stockInDocument, stockOutDocument]) => {
            props.onDocumentCreation(
              // this is already unwrapped RTK query result
              // eslint-disable-next-line eslint-js/no-restricted-syntax
              buildArray<FileResponseBody>()
                .when(stockInDocument.document, stockInDocument.document!)
                .when(stockOutDocument.document, stockOutDocument.document!)
            );
          })
          .catch(handleApiError);
      })
      .catch(handleApiError)
      .finally(() => {
        props.onMovementComplete?.();
      });

  const defaultValues: Partial<VehicleWarehouseTransferFormFields> = {
    vehicleWarehouseId: head(warehouseOptions)?.value,
    movementAt: getApiDateString(new Date()),
  };

  const isLoading = isWarehousesLoading || isTemplatesLoading;
  const isError = isWarehousesError || isTemplatesError;

  return (
    <VStack spacing={4}>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isWithBranchId && isEmptyArray(availableWarehouses)}
        emptyMessage={i18n.t('entity.vehicleWarehouse.labels.noWarehouseForBranch')}
      >
        <Form<VehicleWarehouseTransferFormFields>
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          schema={schema}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <Grid columns={2}>
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.warehouse')}
                  name="vehicleWarehouseId"
                  type="choice"
                  helperText={
                    isWithBranchId
                      ? i18n.t('entity.vehicleWarehouse.labels.showingWarehousesForBranch')
                      : undefined
                  }
                  options={warehouseOptions}
                  control={control}
                  isNotClearable
                  isRequired
                  data-testid={suffixTestId('vehicleWarehouseId-select', props)}
                />
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.dateOfTransfer')}
                  name="movementAt"
                  type="apiDate"
                  control={control}
                  isRequired
                  data-testid={suffixTestId('movementAt', props)}
                />
              </Grid>
              <DocumentTemplateBox
                data-test-id={suffixTestId('stockOut-documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseStockOutDocument')}
                templates={stockOutDefaultTemplates}
                authorizationTemplates={stockOutSelectedTemplates}
                authorizationProfileLabel={stockOutSelectedAuthProfileLabel}
                selectedTemplate={stockOutDocumentTemplate}
                isSelected={isStockOutDocumentSelected}
                onSelect={setIsStockOutDocumentSelected}
                onTemplateChange={setStockOutDocumentTemplate}
                onNoteChange={(value) => formApi.setValue('note', value)}
                noteLabel={i18n.t('entity.document.labels.vehicleWarehouseTransferNote')}
                isWithNote
                isDisabled
              />
              <DocumentTemplateBox
                data-test-id={suffixTestId('stockIn-documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseStockInDocument')}
                templates={stockInDefaultTemplates}
                authorizationTemplates={stockInSelectedTemplates}
                authorizationProfileLabel={stockInSelectedAuthProfileLabel}
                selectedTemplate={stockInDocumentTemplate}
                isSelected={isStockInDocumentSelected}
                onSelect={setIsStockInDocumentSelected}
                onTemplateChange={setStockInDocumentTemplate}
                isDisabled
              />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancel-button', props)}
                />
                <FormButton
                  title={i18n.t('general.actions.confirm')}
                  type="submit"
                  control={control}
                  data-testid={suffixTestId('submit-button', props)}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </VStack>
  );
}

const schema = object({
  vehicleWarehouseId: yupString.required(),
  movementAt: yupString.required(),
  note: yupString,
});
